import React, { useEffect, useRef, useState } from "react";
import { useContextStateManagement } from "../context/ContextApi";
import { DEMO_CALL_STATE, useAgentConfigDemoCall } from "./useAgentConfigDemoCall";
import "./AgentConfigPage.css";

// How long the prompt textarea sits idle before the draft autosaves.
const AUTOSAVE_DELAY_MS = 1200;

// Pseudo tab id for the router/single-agent prompt - never collides with a specialist's id
// since generateSpecialistId() never returns this literal string.
const ROUTER_TAB_ID = "router";

let specialistIdCounter = 0;
function generateSpecialistId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  specialistIdCounter += 1;
  return `specialist-${Date.now()}-${specialistIdCounter}`;
}

function normalizeSpecialist(raw) {
  return {
    id: typeof raw?.id === "string" && raw.id ? raw.id : generateSpecialistId(),
    intent: typeof raw?.intent === "string" ? raw.intent : "",
    prompt: typeof raw?.prompt === "string" ? raw.prompt : "",
  };
}

// Shared shape-normalization for a raw bundle object coming back from either the draft or the
// submitted-snapshot endpoint, so both loading paths (see the initial-load effect) agree on
// exactly what counts as "empty" vs. a real saved value.
function normalizeBundle(bundle) {
  const safeBundle = bundle || {};
  return {
    prompt: typeof safeBundle.prompt === "string" ? safeBundle.prompt : "",
    multiAgentEnabled:
      typeof safeBundle.multiAgentEnabled === "boolean"
        ? safeBundle.multiAgentEnabled
        : false,
    specialists: Array.isArray(safeBundle.specialists)
      ? safeBundle.specialists.map(normalizeSpecialist)
      : [],
  };
}

// Builds the exact JSON that gets persisted as one atomic bundle. `specialists` is kept in the
// bundle even while multi-agent mode is off, so toggling the checkbox off and back on never
// loses anything the user already wrote.
function buildBundle({ prompt, multiAgentEnabled, specialists }) {
  return {
    prompt,
    multiAgentEnabled,
    specialists: specialists.map(({ id, intent, prompt: specialistPrompt }) => ({
      id,
      intent,
      prompt: specialistPrompt,
    })),
  };
}

function serializeSnapshot(snapshot) {
  return JSON.stringify(buildBundle(snapshot));
}

// Issue 8: mm:ss display for an in-progress Demo Call's elapsed time.
function formatDemoCallDuration(totalSeconds) {
  const safeSeconds = Number.isFinite(totalSeconds) && totalSeconds > 0 ? totalSeconds : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatSavedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Issue 4 (redesigned): the AI review returns individually reviewable, located text edits plus
// free-form notes for anything that isn't a precise find-and-replace (see
// server/src/services/agentConfigSuggestionsService.js). Category set matches
// SUGGESTION_CATEGORIES there exactly (Allyzent's house-style prompt-review rule set, adapted
// from prompt-suggestion-agent.xml) - must stay in sync with that file's list/order.
const SUGGESTION_CATEGORY_LABELS = [
  { key: "voiceFormatting", label: "Voice formatting" },
  { key: "conversationFlow", label: "Conversation flow" },
  { key: "accuracy", label: "Accuracy" },
  { key: "clinicalBoundary", label: "Clinical boundary" },
  { key: "layerViolation", label: "Layer violation" },
  { key: "transferConfig", label: "Transfer config" },
  { key: "structure", label: "Structure" },
  { key: "bannedLanguage", label: "Banned language" },
  { key: "grammar", label: "Grammar" },
  { key: "voiceAgentLimitations", label: "Voice-agent limitations" },
];

function hasAnySuggestions(suggestions) {
  if (!suggestions) return false;
  const edits = Array.isArray(suggestions.edits) ? suggestions.edits : [];
  const notes = Array.isArray(suggestions.notes) ? suggestions.notes : [];
  return edits.length > 0 || notes.length > 0;
}

// Groups the flat edits[]/notes[] arrays by category for display, preserving each item's
// original array position as a stable sort key within its category.
function groupSuggestionsByCategory(suggestions) {
  const groups = {};
  for (const { key } of SUGGESTION_CATEGORY_LABELS) {
    groups[key] = { edits: [], notes: [] };
  }
  const edits = Array.isArray(suggestions?.edits) ? suggestions.edits : [];
  const notes = Array.isArray(suggestions?.notes) ? suggestions.notes : [];
  for (const edit of edits) {
    if (groups[edit.category]) groups[edit.category].edits.push(edit);
  }
  for (const note of notes) {
    if (groups[note.category]) groups[note.category].notes.push(note);
  }
  return groups;
}

// Shared title/sensitivity/confidence row for both edit and note cards - sensitivity is the
// signal worth calling out loudest (HIGH means "requires direct human review before applying",
// per the review rubric's own escalation rule), so it renders as a visible tag rather than
// quietly alongside confidence.
function SuggestionHeader({ item }) {
  return (
    <div className="agent-config-suggestion-header">
      <span className="agent-config-suggestion-title">{item.title}</span>
      <span className="agent-config-suggestion-badges">
        {item.sensitivity === "HIGH" ? (
          <span className="agent-config-suggestion-badge agent-config-suggestion-badge--high">
            HIGH sensitivity
          </span>
        ) : null}
        <span
          className={`agent-config-suggestion-badge agent-config-suggestion-badge--confidence-${(
            item.confidence || "MEDIUM"
          ).toLowerCase()}`}
        >
          {item.confidence || "MEDIUM"} confidence
        </span>
      </span>
    </div>
  );
}

// The suggestions panel is now persistent (see handleGenerateSuggestions/handleOpenSuggestionsPanel
// below) - it can stay generated across many edits to the live prompt instead of being tied to a
// single open/close cycle. That means an edit's `located` flag (computed once, server-side, against
// the bundle at generation time) goes stale the moment the user's live edit removes or changes the
// exact oldText a pending edit targets. Re-deriving location against the CURRENT live editor text
// on every render (rather than trusting the frozen flag) is what makes a suggestion automatically
// fall back to "Other Findings" the moment its target text is edited away, instead of staying
// visible as an Accept button that would silently no-op or apply somewhere unintended.
function getCurrentSectionText(prompt, specialists, section) {
  if (section === "router") return prompt || "";
  const match = /^specialist:(\d+)$/.exec(section || "");
  if (!match) return "";
  const specialist = (specialists || [])[Number(match[1])];
  return specialist?.prompt || "";
}

function isEditCurrentlyLocated(edit, prompt, specialists) {
  const sectionText = getCurrentSectionText(prompt, specialists, edit.section);
  return sectionText.length > 0 && sectionText.includes(edit.oldText);
}

// Splits pending suggestions into the two panel sections: individually accept/reject-able diff
// cards (located edits - "git diff type suggestions that can be added immediately") versus
// everything else (unlocated edits + notes - findings that aren't a simple text swap, e.g. a
// missing compliance disclosure), which lives in the collapsed-by-default "Other Findings"
// section instead of being shown as a fake diff or mixed in with the real ones.
function countPendingSuggestions(suggestions, resolvedIds, prompt, specialists) {
  const edits = Array.isArray(suggestions?.edits) ? suggestions.edits : [];
  const notes = Array.isArray(suggestions?.notes) ? suggestions.notes : [];
  const pendingLocatedEdits = edits.filter(
    (edit) => isEditCurrentlyLocated(edit, prompt, specialists) && !resolvedIds.has(edit.id),
  );
  const pendingUnlocatedEdits = edits.filter(
    (edit) => !isEditCurrentlyLocated(edit, prompt, specialists) && !resolvedIds.has(edit.id),
  );
  const pendingNotes = notes.filter((note) => !resolvedIds.has(note.id));
  const pendingOtherFindings = [...pendingUnlocatedEdits, ...pendingNotes];
  return {
    pendingLocatedEdits,
    pendingOtherFindings,
    totalLocated: pendingLocatedEdits.length,
    totalOther: pendingOtherFindings.length,
  };
}

// Issue 5: short, single-line preview of a history entry's bundle - just enough for an admin to
// recognize which version is which without opening it. Multi-agent bundles fall back to a
// specialist count since there is no single "the prompt" to preview.
function summarizeBundleForHistory(bundle) {
  const safeBundle = bundle || {};
  const routerPrompt = typeof safeBundle.prompt === "string" ? safeBundle.prompt.trim() : "";
  const specialistCount = Array.isArray(safeBundle.specialists)
    ? safeBundle.specialists.length
    : 0;

  if (routerPrompt) {
    const truncated =
      routerPrompt.length > 140 ? `${routerPrompt.slice(0, 140).trim()}...` : routerPrompt;
    return specialistCount > 0
      ? `${truncated} (+${specialistCount} specialist${specialistCount === 1 ? "" : "s"})`
      : truncated;
  }

  return specialistCount > 0
    ? `${specialistCount} specialist prompt${specialistCount === 1 ? "" : "s"}, no router prompt`
    : "(empty)";
}

// New standalone "Agent Config" page (PRD: per-practice prompt/config version control).
// Issue 1 shipped a single-prompt draft editor. Issue 2 extends the same bundle/draft row with
// an optional multi-agent mode: a router prompt (the same top-level `prompt` field, reused
// as-is) plus a tab per specialist, matching Prompt Hub's existing router + per-intent
// AgentConfigs concept. Everything - the toggle, the router tab, and every specialist tab -
// still autosaves as a single JSON bundle to POST /api/agentConfig/draft/:practiceId; switching
// tabs is pure local UI state and never triggers a save by itself, so it can never create a
// second draft row for the practice. Issue 3 adds Submit Draft (POST
// /api/agentConfig/submit/:practiceId, locking the current bundle into a testable "submitted"
// snapshot) and a Demo Call button whose color reflects whether that submitted snapshot still
// matches the live editor content. Issue 4 adds an AI Suggestions button that calls the new
// Bedrock-backed (Anthropic-direct-fallback) review endpoint against the current submitted
// draft, shows the grouped critique (clarity / technical correctness / grammar / voice-agent
// limitations) in a side panel, and gates a new Publish button on that critique having been
// generated and viewed at least once for the current submitted draft - Publish's actual
// publish/rollback/live-pointer behavior is a later issue in the version-control PRD, so its
// click handler here is a placeholder (matches how Issue 3 left Demo Call's actual session
// start as a placeholder). API integration fields and actually starting a demo session remain
// out of scope here. Prompt Hub is untouched.
export const AgentConfigPage = () => {
  const {
    PracticeId,
    getAgentConfigDraft,
    saveAgentConfigDraft,
    submitAgentConfigDraft,
    getSubmittedAgentConfig,
    generateAgentConfigSuggestions,
    getAgentConfigSuggestions,
    markAgentConfigSuggestionsViewed,
    publishAgentConfigDraft,
    getAgentConfigHistory,
    getAgentConfigCurrentLive,
    rollbackAgentConfigVersion,
    getAgentConfigTestPhoneNumber,
    saveAgentConfigTestPhoneNumber,
    startAgentConfigPhoneDemoCall,
  } = useContextStateManagement();

  // Issue 5: which top-level page tab is showing - the existing editor, or the new version
  // History tab. Purely local UI state, never persisted.
  const [activePageTab, setActivePageTab] = useState("editor");

  const [prompt, setPrompt] = useState("");
  const [multiAgentEnabled, setMultiAgentEnabled] = useState(false);
  const [specialists, setSpecialists] = useState([]);
  const [activeTabId, setActiveTabId] = useState(ROUTER_TAB_ID);

  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Issue 3: submit-draft + Demo Call button state. `submittedSnapshotJson` is null until the
  // practice has a submitted row at all; once it exists, the Demo Call button is green when it
  // matches the live editor content and red the moment any edit makes them differ - it only
  // flips back to green on the next successful submit.
  const [submitState, setSubmitState] = useState("idle");
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [lastSubmittedAt, setLastSubmittedAt] = useState(null);
  const [submittedSnapshotJson, setSubmittedSnapshotJson] = useState(null);

  // Issue 8: Demo Call - in-browser mode. The hook owns the LiveKit room/mic lifecycle; this
  // page only decides WHEN it's allowed to start (a submitted draft must exist) and how the
  // button/status area reflect its state.
  const {
    demoCallState,
    demoCallError,
    demoCallDuration,
    demoCallVersionId,
    startDemoCall,
    endDemoCall,
  } = useAgentConfigDemoCall(PracticeId);

  // Issue 9: Demo Call - phone callback mode. The test number is saved separately from the
  // prompt editor (an explicit Save, not autosaved with the draft) since it's a standing
  // per-practice setting, not versioned bundle content - see agentConfigController.js /
  // migration 091. `testPhoneNumberSavedValue` is what the backend currently has on file;
  // `testPhoneNumber` is the (possibly-unsaved) text in the input.
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [testPhoneNumberSavedValue, setTestPhoneNumberSavedValue] = useState(null);
  const [testPhoneNumberSaveState, setTestPhoneNumberSaveState] = useState("idle");
  const [testPhoneNumberError, setTestPhoneNumberError] = useState("");
  const [phoneDemoCallState, setPhoneDemoCallState] = useState("idle");
  const [phoneDemoCallError, setPhoneDemoCallError] = useState("");
  const [phoneDemoCallMessage, setPhoneDemoCallMessage] = useState("");

  // Issue 4: AI Suggestions + Publish-button gate. `suggestions` / `suggestionsViewed` mirror
  // the current submitted draft's SuggestionsJson / SuggestionsViewedAt columns exactly - both
  // reset to null/false the moment a fresh submit or a fresh "Generate" call lands on the
  // backend (see agentConfigController.js), so Publish re-locks automatically without any extra
  // client-side bookkeeping.
  const [suggestionsState, setSuggestionsState] = useState("idle");
  const [suggestionsErrorMessage, setSuggestionsErrorMessage] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsGeneratedAt, setSuggestionsGeneratedAt] = useState(null);
  const [suggestionsViewed, setSuggestionsViewed] = useState(false);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  // Ids (see agentConfigSuggestionsService.js's `edit-N`/`note-N`) the user has already accepted
  // or rejected in THIS viewing of the panel - purely client-side, never persisted, since once
  // applied an edit is just part of the prompt text and once rejected it's not worth remembering
  // past this session. Reset whenever a fresh set of suggestions is generated.
  const [resolvedSuggestionIds, setResolvedSuggestionIds] = useState(() => new Set());
  // "Other Findings" (unlocated edits + notes - anything that isn't a simple, immediately
  // add-able diff) is collapsed by default; the diff cards are the panel's primary content.
  const [showOtherFindings, setShowOtherFindings] = useState(false);
  const [publishMessage, setPublishMessage] = useState("");

  // Issue 5: Publish + version history + rollback. `historyVersions`/`liveVersionId` mirror the
  // GET /api/agentConfig/history/:practiceId response exactly - `liveVersionId` comes from the
  // shared ConfigLivePointers pointer (server/src/services/configLivePointerService.js), not
  // from anything derived client-side, since another system (e.g. a coexisting Prompt Hub
  // publish) could in principle move it too.
  const [publishState, setPublishState] = useState("idle");
  const [historyVersions, setHistoryVersions] = useState([]);
  const [liveVersionId, setLiveVersionId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErrorMessage, setHistoryErrorMessage] = useState("");
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState(null);
  const [rollbackState, setRollbackState] = useState("idle");

  // Published-prompt visibility: what's ACTUALLY live for real calls right now, regardless of
  // whether it was last published from this page or the legacy Prompt Hub (GET
  // /api/agentConfig/currentLive/:practiceId - resolves both sources, unlike the LiveKit agent's
  // own /live endpoint which only trusts "agent-config"). Read-only display, refetched after
  // publish/rollback since either one can change what's live.
  const [currentLive, setCurrentLive] = useState(null);
  const [currentLiveLoading, setCurrentLiveLoading] = useState(false);
  const [showCurrentLive, setShowCurrentLive] = useState(false);

  const refreshCurrentLive = async () => {
    if (!PracticeId) return;
    setCurrentLiveLoading(true);
    try {
      const response = await getAgentConfigCurrentLive(PracticeId);
      setCurrentLive(response || null);
    } catch (error) {
      setCurrentLive(null);
    } finally {
      setCurrentLiveLoading(false);
    }
  };
  const [rollbackErrorMessage, setRollbackErrorMessage] = useState("");

  const lastSavedSnapshotRef = useRef(
    serializeSnapshot({ prompt: "", multiAgentEnabled: false, specialists: [] }),
  );
  const debounceRef = useRef(null);
  const saveStateRef = useRef("idle");
  const snapshotRef = useRef({ prompt: "", multiAgentEnabled: false, specialists: [] });

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  useEffect(() => {
    snapshotRef.current = { prompt, multiAgentEnabled, specialists };
  }, [prompt, multiAgentEnabled, specialists]);

  const persistBundle = async (snapshot) => {
    if (!PracticeId) return;
    setSaveState("saving");
    try {
      const response = await saveAgentConfigDraft(PracticeId, buildBundle(snapshot));
      lastSavedSnapshotRef.current = serializeSnapshot(snapshot);
      setSaveState("saved");
      setLastSavedAt(response?.updatedAt || new Date().toISOString());
      setErrorMessage("");
    } catch (error) {
      setSaveState("error");
      setErrorMessage(
        error?.message ||
          "Couldn't save the draft. Your changes are kept locally - try Save Draft again.",
      );
    }
  };

  // Re-fetches the practice's full publish history + live-pointer state (Issue 5). Called on
  // initial load, after a successful Publish, and after a successful Rollback, so the History
  // tab and the "currently live" badge never go stale relative to what the backend just did.
  const refreshHistory = async () => {
    if (!PracticeId) return;
    setHistoryLoading(true);
    try {
      const response = await getAgentConfigHistory(PracticeId);
      setHistoryVersions(Array.isArray(response?.versions) ? response.versions : []);
      setLiveVersionId(response?.liveVersionId ?? null);
      setHistoryErrorMessage("");
    } catch (error) {
      setHistoryErrorMessage(
        error?.message || "Couldn't load version history. Try again.",
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load the practice's current draft (if any) whenever the practice context changes. Also
  // loads the current submitted snapshot (if any) in the same pass, both so the Demo Call
  // button can show the right color immediately and so a page reload right after a submit -
  // when there is no draft row yet, see Issue 3's submit endpoint - starts the editor from the
  // submitted content instead of showing a blank page.
  useEffect(() => {
    if (!PracticeId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setInitialLoadDone(false);
    setErrorMessage("");

    Promise.all([
      getAgentConfigDraft(PracticeId),
      // A failure here shouldn't block loading the draft itself - worst case the Demo Call
      // button just starts in its "never submitted" disabled state.
      getSubmittedAgentConfig(PracticeId).catch(() => null),
      // Same non-blocking treatment for suggestions state - worst case Publish just starts
      // gated (the safe default) until the user regenerates suggestions.
      getAgentConfigSuggestions(PracticeId).catch(() => null),
      // Non-blocking too - worst case the History tab shows its own retry rather than blocking
      // the editor from loading.
      getAgentConfigHistory(PracticeId).catch(() => null),
      // Issue 9: non-blocking - worst case the test-phone-number field just starts blank and
      // "Call This Number" stays disabled until the user saves one.
      getAgentConfigTestPhoneNumber(PracticeId).catch(() => null),
      // Non-blocking - worst case the "Currently published" section just starts empty/hidden.
      getAgentConfigCurrentLive(PracticeId).catch(() => null),
    ])
      .then(([draft, submitted, suggestionsResponse, historyResponse, testPhoneNumberResponse, currentLiveResponse]) => {
        if (cancelled) return;

        const submittedBundle = submitted?.exists
          ? normalizeBundle(submitted.bundle)
          : null;
        const startingBundle = draft?.exists
          ? normalizeBundle(draft.bundle)
          : submittedBundle || normalizeBundle({});

        setPrompt(startingBundle.prompt);
        setMultiAgentEnabled(startingBundle.multiAgentEnabled);
        setSpecialists(startingBundle.specialists);
        setActiveTabId(ROUTER_TAB_ID);

        lastSavedSnapshotRef.current = serializeSnapshot(startingBundle);
        setLastSavedAt(draft?.updatedAt || submitted?.submittedAt || null);

        setSubmittedSnapshotJson(
          submittedBundle ? serializeSnapshot(submittedBundle) : null,
        );
        setLastSubmittedAt(submitted?.submittedAt || null);

        setSuggestions(suggestionsResponse?.suggestions || null);
        setSuggestionsGeneratedAt(suggestionsResponse?.generatedAt || null);
        setSuggestionsViewed(Boolean(suggestionsResponse?.viewed));

        setHistoryVersions(
          Array.isArray(historyResponse?.versions) ? historyResponse.versions : [],
        );
        setLiveVersionId(historyResponse?.liveVersionId ?? null);

        const savedTestPhoneNumber = testPhoneNumberResponse?.testPhoneNumber || "";
        setTestPhoneNumber(savedTestPhoneNumber);
        setTestPhoneNumberSavedValue(savedTestPhoneNumber || null);

        setCurrentLive(currentLiveResponse || null);

        setSaveState("idle");
      })
      .catch((error) => {
        if (cancelled) return;
        setErrorMessage(error?.message || "Failed to load the saved draft.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId]);

  // Autosave: debounce edits to the router prompt, the multi-agent toggle, or any specialist's
  // intent/prompt - but only once the initial draft load has finished, so we never overwrite a
  // saved draft while it's still loading. Switching the active tab does not touch any of these
  // three pieces of state, so it never schedules a save on its own.
  useEffect(() => {
    if (!initialLoadDone || !PracticeId) return undefined;

    const currentSnapshot = { prompt, multiAgentEnabled, specialists };
    if (serializeSnapshot(currentSnapshot) === lastSavedSnapshotRef.current) {
      return undefined;
    }

    setSaveState("unsaved");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      persistBundle(currentSnapshot);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt, multiAgentEnabled, specialists, initialLoadDone, PracticeId]);

  // Best-effort flush of a pending edit if the user navigates away before the debounce fires.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (
        saveStateRef.current === "unsaved" ||
        saveStateRef.current === "saving"
      ) {
        persistBundle(snapshotRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    persistBundle({ prompt, multiAgentEnabled, specialists });
  };

  // Locks whatever is currently in the editor into a submitted snapshot - passing the bundle
  // explicitly (rather than relying on the last autosave having landed) means Submit Draft
  // never races the autosave debounce. See agentConfigController.js for why this also keeps
  // editing afterward from ever overwriting the submitted row until Submit Draft runs again.
  const handleSubmitDraft = async () => {
    if (!PracticeId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const currentSnapshot = { prompt, multiAgentEnabled, specialists };
    setSubmitState("submitting");
    setSubmitErrorMessage("");
    try {
      const response = await submitAgentConfigDraft(
        PracticeId,
        buildBundle(currentSnapshot),
      );
      const submittedJson = serializeSnapshot(currentSnapshot);

      // The backend locks this exact content into the (now-submitted) draft row too, so treat
      // it as already saved - otherwise the next autosave tick would immediately re-POST the
      // same content into a brand-new draft row for no reason.
      lastSavedSnapshotRef.current = submittedJson;
      setSaveState("saved");
      setLastSavedAt(response?.submittedAt || new Date().toISOString());

      setSubmittedSnapshotJson(submittedJson);
      setLastSubmittedAt(response?.submittedAt || new Date().toISOString());
      setSubmitState("idle");

      // A submit always lands on a brand-new 'submitted' row on the backend (see
      // agentConfigController.js), so any suggestions/viewed state from a *previous* submitted
      // row no longer applies - Publish must re-lock until AI Suggestions is generated and
      // viewed again for this new draft.
      setSuggestions(null);
      setSuggestionsGeneratedAt(null);
      setSuggestionsViewed(false);
      setResolvedSuggestionIds(new Set());
      setShowOtherFindings(false);
      setShowSuggestionsPanel(false);
      setPublishMessage("");
    } catch (error) {
      setSubmitState("error");
      setSubmitErrorMessage(
        error?.message || "Couldn't submit the draft. Try again.",
      );
    }
  };

  // Calls the Bedrock-backed (Anthropic-direct-fallback) review endpoint against the current
  // submitted draft and opens the suggestions panel. A fresh call always resets the "viewed"
  // flag server-side (see agentConfigController.js), so Publish re-locks until the panel is
  // dismissed again even on a re-generate against the same submitted draft.
  const handleGenerateSuggestions = async () => {
    if (!PracticeId) return;
    setSuggestionsState("generating");
    setSuggestionsErrorMessage("");
    try {
      const response = await generateAgentConfigSuggestions(PracticeId);
      setSuggestions(response?.suggestions || null);
      setSuggestionsGeneratedAt(response?.generatedAt || new Date().toISOString());
      setSuggestionsViewed(Boolean(response?.viewed));
      setResolvedSuggestionIds(new Set());
      setShowOtherFindings(false);
      setShowSuggestionsPanel(true);
      setSuggestionsState("idle");
    } catch (error) {
      setSuggestionsState("error");
      setSuggestionsErrorMessage(
        error?.message ||
          "Couldn't generate AI suggestions right now. Try again in a moment.",
      );
    }
  };

  // The toolbar "AI Suggestions" button: reopen the already-generated set (no API call, same
  // suggestions the user was looking at before) unless nothing has been generated yet for this
  // submitted draft (suggestions === null - either never generated, or reset by a fresh Submit).
  // Regenerating a still-open set is an explicit, separate action (see the panel's own
  // "Regenerate" button) so minimizing/reopening never silently throws away what's on screen.
  const handleOpenOrGenerateSuggestions = () => {
    if (suggestions !== null) {
      setShowSuggestionsPanel(true);
      return;
    }
    handleGenerateSuggestions();
  };

  // Minimizing tucks the panel into the sidebar tab without touching the suggestions data or the
  // "viewed" flag - purely a local visibility toggle, so editing the prompt afterward never loses
  // or regenerates anything.
  const handleMinimizeSuggestions = () => {
    setShowSuggestionsPanel(false);
  };

  // Dismissing/continuing past the suggestions panel is what actually sets the "viewed" flag
  // Publish is gated on - the panel merely being generated and shown is not enough on its own.
  const handleDismissSuggestions = async () => {
    setShowSuggestionsPanel(false);
    if (!PracticeId) return;
    try {
      const response = await markAgentConfigSuggestionsViewed(PracticeId);
      setSuggestionsViewed(Boolean(response?.viewed ?? true));
    } catch (error) {
      setSuggestionsErrorMessage(
        error?.message ||
          "Couldn't record that the suggestions were viewed - Publish may still be locked. Reopen AI Suggestions to try again.",
      );
    }
  };

  // Applies one edit's oldText -> newText replacement to the right field (router prompt, or a
  // specific specialist's prompt) per the edit's own `section` - independent of whichever editor
  // tab happens to be active, so accepting an edit for a specialist the user isn't currently
  // viewing still lands correctly. Uses the functional setState form so accepting several edits
  // to the same field in one go (see handleAcceptAllEdits) chains correctly instead of each call
  // clobbering the others with a stale closure value.
  const applyEditToBundle = (edit) => {
    if (edit.section === "router") {
      setPrompt((current) => current.replace(edit.oldText, edit.newText));
      return;
    }
    const specialistMatch = /^specialist:(\d+)$/.exec(edit.section);
    if (specialistMatch) {
      const index = Number(specialistMatch[1]);
      setSpecialists((current) =>
        current.map((specialist, i) =>
          i === index
            ? { ...specialist, prompt: specialist.prompt.replace(edit.oldText, edit.newText) }
            : specialist,
        ),
      );
    }
  };

  const handleAcceptEdit = (edit) => {
    applyEditToBundle(edit);
    setResolvedSuggestionIds((current) => new Set(current).add(edit.id));
  };

  const handleRejectSuggestion = (suggestion) => {
    setResolvedSuggestionIds((current) => new Set(current).add(suggestion.id));
  };

  // Accept all / Reject all (top of panel) act only on the visible diff cards (located edits) -
  // "Other Findings" is a separate, collapsed-by-default section with its own dismiss-all, so
  // rejecting the diffs you can see never silently dismisses findings you haven't opened yet.
  const handleAcceptAllEdits = () => {
    const { pendingLocatedEdits } = countPendingSuggestions(
      suggestions,
      resolvedSuggestionIds,
      prompt,
      specialists,
    );
    pendingLocatedEdits.forEach(applyEditToBundle);
    setResolvedSuggestionIds((current) => {
      const next = new Set(current);
      pendingLocatedEdits.forEach((edit) => next.add(edit.id));
      return next;
    });
  };

  const handleRejectAllSuggestions = () => {
    const { pendingLocatedEdits } = countPendingSuggestions(
      suggestions,
      resolvedSuggestionIds,
      prompt,
      specialists,
    );
    setResolvedSuggestionIds((current) => {
      const next = new Set(current);
      pendingLocatedEdits.forEach((edit) => next.add(edit.id));
      return next;
    });
  };

  const handleDismissAllOtherFindings = () => {
    const { pendingOtherFindings } = countPendingSuggestions(
      suggestions,
      resolvedSuggestionIds,
      prompt,
      specialists,
    );
    setResolvedSuggestionIds((current) => {
      const next = new Set(current);
      pendingOtherFindings.forEach((item) => next.add(item.id));
      return next;
    });
  };

  // Publish (Issue 5): moves the current submitted draft live and repoints the shared
  // ConfigLivePointers pointer at it. The backend re-checks the suggestions-viewed gate itself,
  // so a stale client-side gate (e.g. two tabs open) still surfaces the backend's own error
  // rather than silently succeeding.
  //
  // Once published, the submitted row this practice had is no longer "submitted" (it moved to
  // 'published' in place - see agentConfigController.js) - so the Demo Call / AI Suggestions /
  // Publish buttons all reset back to their "nothing submitted yet" state, exactly as if the
  // practice had never submitted, until the next Save Draft -> Submit Draft cycle.
  const handlePublishClick = async () => {
    if (!PracticeId || !publishEnabled) return;
    setPublishState("publishing");
    setPublishMessage("");
    try {
      const response = await publishAgentConfigDraft(PracticeId);

      setSubmittedSnapshotJson(null);
      setLastSubmittedAt(null);
      setSuggestions(null);
      setSuggestionsGeneratedAt(null);
      setSuggestionsViewed(false);
      setResolvedSuggestionIds(new Set());
      setShowOtherFindings(false);
      setShowSuggestionsPanel(false);

      const publishedAtLabel = formatSavedAt(response?.publishedAt);
      setPublishMessage(
        `Published${response?.versionId ? ` as version ${response.versionId}` : ""}${
          publishedAtLabel ? ` - ${publishedAtLabel}` : ""
        }. Real calls will use this version going forward.`,
      );
      setPublishState("idle");

      await refreshHistory();
      await refreshCurrentLive();
    } catch (error) {
      setPublishState("error");
      setPublishMessage(
        error?.message || "Couldn't publish this draft. Try again.",
      );
    }
  };

  // Opens the History tab and makes sure its list reflects the latest publish/rollback -
  // cheap enough to always refetch rather than trust whatever was loaded on page load.
  const handleOpenHistoryTab = () => {
    setActivePageTab("history");
    refreshHistory();
  };

  const handleRequestRollback = (version) => {
    setRollbackErrorMessage("");
    setRollbackTargetVersion(version);
  };

  const handleCancelRollback = () => {
    setRollbackTargetVersion(null);
  };

  // Rollback (Issue 5): clones an old published version's content into a brand-new published
  // version and repoints the live pointer at it - the old version stays in history untouched
  // (see agentConfigController.js), so this never destructively reverts anything.
  const handleConfirmRollback = async () => {
    if (!PracticeId || !rollbackTargetVersion) return;
    setRollbackState("rolling-back");
    setRollbackErrorMessage("");
    try {
      await rollbackAgentConfigVersion(PracticeId, rollbackTargetVersion.versionId);
      setRollbackTargetVersion(null);
      setRollbackState("idle");
      await refreshHistory();
      await refreshCurrentLive();
    } catch (error) {
      setRollbackState("error");
      setRollbackErrorMessage(
        error?.message || "Couldn't roll back to that version. Try again.",
      );
    }
  };

  // Demo Call (Issue 8): starts/ends an in-browser mic session against the practice's current
  // submitted draft. Requesting mic access and joining the LiveKit room is the hook's job
  // (useAgentConfigDemoCall) - this only gates "may we start" (a submitted draft must exist,
  // matching the button's own green/red/disabled state below) and dispatches to whichever
  // action makes sense for the call's current state; while connecting/ending the button is
  // disabled so this is never called mid-transition.
  const handleDemoCallClick = () => {
    if (demoCallState === DEMO_CALL_STATE.IN_CALL) {
      endDemoCall();
      return;
    }
    if (demoCallState === DEMO_CALL_STATE.IDLE && hasSubmission) {
      startDemoCall();
    }
  };

  // Issue 9: saves (or, on a blank field, clears) the sticky test phone number. An explicit
  // action rather than autosave - unlike the prompt editor, this is a low-frequency setting, and
  // "Call This Number" always dials whatever was last saved here, so a half-typed number should
  // never silently become the dialed number without the user choosing to save it.
  const handleSaveTestPhoneNumber = async () => {
    if (!PracticeId) return;
    setTestPhoneNumberSaveState("saving");
    setTestPhoneNumberError("");
    try {
      const response = await saveAgentConfigTestPhoneNumber(PracticeId, testPhoneNumber);
      const saved = response?.testPhoneNumber || "";
      setTestPhoneNumber(saved);
      setTestPhoneNumberSavedValue(saved || null);
      setTestPhoneNumberSaveState("saved");
    } catch (error) {
      setTestPhoneNumberSaveState("error");
      setTestPhoneNumberError(
        error?.message || "Couldn't save that number. Try again.",
      );
    }
  };

  // Places a real outbound call to the saved test number via the practice's telephony trunk -
  // the real path /api/dialerCall and the lead-generation dialer already use, running the
  // current SUBMITTED draft bundle. There is no browser/LiveKit room for this tab to join, so
  // unlike the in-browser Demo Call above there is no live "connected" state to track here -
  // once the backend has dispatched the call, this page's job is done; whoever answers hears the
  // draft.
  const handleCallThisNumberClick = async () => {
    if (!PracticeId || !canCallTestNumber) return;
    setPhoneDemoCallState("calling");
    setPhoneDemoCallError("");
    setPhoneDemoCallMessage("");
    try {
      const response = await startAgentConfigPhoneDemoCall(PracticeId);
      setPhoneDemoCallMessage(
        `Calling ${response?.toNumber || testPhoneNumberSavedValue} now${
          response?.submittedVersionId ? ` - testing submitted version ${response.submittedVersionId}` : ""
        }. Real calls for this practice keep using the published version until you publish.`,
      );
      setPhoneDemoCallState("idle");
    } catch (error) {
      setPhoneDemoCallState("error");
      setPhoneDemoCallError(
        error?.message || "Couldn't place the call. Try again.",
      );
    }
  };

  const handleToggleMultiAgent = (checked) => {
    setMultiAgentEnabled(checked);
    setActiveTabId(ROUTER_TAB_ID);
  };

  const handleAddSpecialist = () => {
    const newSpecialist = normalizeSpecialist({});
    setSpecialists((current) => [...current, newSpecialist]);
    setActiveTabId(newSpecialist.id);
  };

  const handleRemoveSpecialist = (id) => {
    setSpecialists((current) => current.filter((specialist) => specialist.id !== id));
    setActiveTabId((current) => (current === id ? ROUTER_TAB_ID : current));
  };

  const handleSpecialistIntentChange = (id, value) => {
    setSpecialists((current) =>
      current.map((specialist) =>
        specialist.id === id ? { ...specialist, intent: value } : specialist,
      ),
    );
  };

  const handleSpecialistPromptChange = (id, value) => {
    setSpecialists((current) =>
      current.map((specialist) =>
        specialist.id === id ? { ...specialist, prompt: value } : specialist,
      ),
    );
  };

  const savedAtLabel = formatSavedAt(lastSavedAt);

  const statusMeta = {
    idle: { label: "No changes yet", className: "agent-config-status--idle" },
    unsaved: {
      label: "Unsaved changes",
      className: "agent-config-status--unsaved",
    },
    saving: { label: "Saving...", className: "agent-config-status--saving" },
    saved: {
      label: savedAtLabel
        ? `All changes saved - ${savedAtLabel}`
        : "All changes saved",
      className: "agent-config-status--saved",
    },
    error: {
      label: "Couldn't save - will retry",
      className: "agent-config-status--error",
    },
  }[saveState] || { label: "", className: "" };

  // Demo Call button state (Issue 3, extended by Issue 8): green once the submitted snapshot
  // matches exactly what's in the editor right now, red the moment any edit makes them differ,
  // and disabled until the practice has submitted at least once (nothing exists yet to demo).
  // Once a session is actually connecting/live/ending (Issue 8), that call-lifecycle state takes
  // over the button's label/color/title entirely - the submitted-vs-editor divergence only
  // matters again once the call is back to idle.
  const currentSnapshotJson = serializeSnapshot({ prompt, multiAgentEnabled, specialists });
  const hasSubmission = submittedSnapshotJson !== null;
  const isDirtySinceSubmit = hasSubmission && currentSnapshotJson !== submittedSnapshotJson;
  const lastSubmittedAtLabel = formatSavedAt(lastSubmittedAt);
  const demoCallDurationLabel = formatDemoCallDuration(demoCallDuration);
  const demoCallIdle = demoCallState === DEMO_CALL_STATE.IDLE;

  const demoCallTitle = !demoCallIdle
    ? {
        [DEMO_CALL_STATE.CONNECTING]: "Requesting mic access and connecting...",
        [DEMO_CALL_STATE.IN_CALL]: "End this demo call",
        [DEMO_CALL_STATE.ENDING]: "Ending the demo call...",
      }[demoCallState]
    : !hasSubmission
      ? "Submit a draft first to enable Demo Call"
      : isDirtySinceSubmit
        ? "Draft has changed since the last submit - submit again to test the latest content"
        : `Submitted draft matches the editor${
            lastSubmittedAtLabel ? ` - submitted ${lastSubmittedAtLabel}` : ""
          }`;

  const demoCallClassName = !demoCallIdle
    ? demoCallState === DEMO_CALL_STATE.IN_CALL
      ? "agent-config-demo-call-btn--active"
      : "agent-config-demo-call-btn--connecting"
    : !hasSubmission
      ? "agent-config-demo-call-btn--disabled"
      : isDirtySinceSubmit
        ? "agent-config-demo-call-btn--red"
        : "agent-config-demo-call-btn--green";

  const demoCallLabel =
    {
      [DEMO_CALL_STATE.CONNECTING]: "Connecting...",
      [DEMO_CALL_STATE.IN_CALL]: `End Demo Call (${demoCallDurationLabel})`,
      [DEMO_CALL_STATE.ENDING]: "Ending...",
    }[demoCallState] || "Demo Call";

  const demoCallButtonDisabled =
    demoCallState === DEMO_CALL_STATE.CONNECTING ||
    demoCallState === DEMO_CALL_STATE.ENDING ||
    (demoCallIdle && !hasSubmission);

  // Demo Call - phone callback mode (Issue 9): "Save Number" only needs a non-blank, changed
  // value; "Call This Number" needs a submitted draft (same precondition as the in-browser Demo
  // Call button) AND a number already saved server-side - typing into the field alone must
  // never make the button callable until Save Number has actually landed.
  const testPhoneNumberDirty = testPhoneNumber.trim() !== (testPhoneNumberSavedValue || "");
  const saveTestPhoneNumberDisabled =
    testPhoneNumberSaveState === "saving" || !testPhoneNumberDirty;
  const canCallTestNumber =
    hasSubmission && Boolean(testPhoneNumberSavedValue) && phoneDemoCallState !== "calling";
  const callThisNumberTitle = !hasSubmission
    ? "Submit a draft first to enable Call This Number"
    : !testPhoneNumberSavedValue
      ? "Save a test phone number first"
      : phoneDemoCallState === "calling"
        ? "Placing the call..."
        : `Calls ${testPhoneNumberSavedValue} and runs it against the current submitted draft`;

  // AI Suggestions button (Issue 4): needs a submitted draft to review, same precondition as
  // Demo Call. Once a set exists (suggestions !== null), this button just reopens it - it never
  // silently regenerates, so it can't throw away a set the user is still working through.
  const suggestionsButtonDisabled = !hasSubmission || suggestionsState === "generating";
  const suggestionsButtonTitle = !hasSubmission
    ? "Submit a draft first to enable AI Suggestions"
    : suggestionsState === "generating"
      ? "Generating suggestions..."
      : suggestions !== null
        ? "Reopen the current AI suggestions (use Regenerate inside the panel for a fresh review)"
        : "Get AI feedback on the current submitted draft (clarity, technical correctness, grammar, voice-agent limitations)";

  // Publish button gate (Issue 4's acceptance criterion, enforced again server-side by Issue
  // 5's publish endpoint): clickable only once suggestions have been generated AND viewed for
  // the current submitted draft, and not while a publish call is already in flight.
  const publishEnabled =
    hasSubmission && suggestionsViewed && publishState !== "publishing";
  const publishTitle = !hasSubmission
    ? "Submit a draft first to enable Publish"
    : !suggestionsViewed
      ? "View AI Suggestions for this draft before publishing"
      : publishState === "publishing"
        ? "Publishing..."
        : "Ready to publish - suggestions have been viewed for this draft";

  const liveVersionEntry = historyVersions.find((v) => v.versionId === liveVersionId) || null;
  const liveVersionLabel = liveVersionEntry
    ? formatSavedAt(liveVersionEntry.publishedAt)
    : null;

  if (!PracticeId) {
    return (
      <div className="agent-config-page p-4 text-light">
        Select or load a practice to edit its Agent Config.
      </div>
    );
  }

  const activeSpecialist =
    multiAgentEnabled && activeTabId !== ROUTER_TAB_ID
      ? specialists.find((specialist) => specialist.id === activeTabId) || null
      : null;

  // Same router textarea whether multi-agent mode is off or the Router tab is active - it is
  // always bound to the same top-level `prompt` state either way.
  const routerTextarea = (
    <textarea
      id="agentConfigPrompt"
      className="agent-config-textarea"
      value={prompt}
      onChange={(event) => setPrompt(event.target.value)}
      placeholder={
        multiAgentEnabled
          ? "Write the router's instructions - greet the caller and decide which specialist should handle their request..."
          : "Write the instructions your voice agent should follow on calls..."
      }
      spellCheck
    />
  );

  return (
    <div className="agent-config-page p-4">
      <div className="agent-config-header">
        <h2 className="agent-config-title">Agent Config</h2>
        <p className="agent-config-subtitle">
          Edit your agent&apos;s prompt as a draft. Enable multi-agent mode to add a
          router prompt plus specialist tabs - everything here still autosaves as one
          draft and never affects live calls until it is published.
        </p>
        {liveVersionEntry ? (
          <p className="agent-config-live-indicator">
            Currently live: version {liveVersionEntry.versionId}
            {liveVersionLabel ? ` - published ${liveVersionLabel}` : ""}
          </p>
        ) : null}
      </div>

      {/* Published-prompt visibility: what's ACTUALLY live for real calls, resolved regardless
          of source (this page's own publish/rollback, or the legacy Prompt Hub) - the small
          "Currently live: version N" line above only ever reflects an Agent-Config-sourced
          publish, so a practice whose live prompt still comes from Prompt Hub would otherwise
          show nothing at all here. */}
      {currentLive?.exists ? (
        <div className="agent-config-card agent-config-current-live-card">
          <div
            className="agent-config-card-header agent-config-current-live-toggle"
            role="button"
            tabIndex={0}
            onClick={() => setShowCurrentLive((current) => !current)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setShowCurrentLive((current) => !current);
              }
            }}
          >
            <label className="agent-config-label">
              {showCurrentLive ? "▾" : "▸"} Currently published (live for real calls)
            </label>
            <span className="agent-config-current-live-source">
              {currentLive.source === "prompt-hub" ? "Published from Prompt Hub" : "Published from Agent Config"}
              {formatSavedAt(currentLive.publishedAt) ? ` - ${formatSavedAt(currentLive.publishedAt)}` : ""}
              {currentLiveLoading ? " (refreshing...)" : ""}
            </span>
          </div>

          {showCurrentLive ? (
            <div className="agent-config-current-live-body">
              <div className="agent-config-current-live-section">
                <div className="agent-config-current-live-section-title">
                  {currentLive.multiAgentEnabled ? "Router prompt" : "Prompt"}
                </div>
                <pre className="agent-config-current-live-text">
                  {currentLive.prompt || "(empty)"}
                </pre>
              </div>
              {currentLive.multiAgentEnabled
                ? currentLive.specialists.map((specialist, index) => (
                    <div key={`${specialist.intent}-${index}`} className="agent-config-current-live-section">
                      <div className="agent-config-current-live-section-title">
                        Specialist: {specialist.intent || `#${index + 1}`}
                      </div>
                      <pre className="agent-config-current-live-text">
                        {specialist.prompt || "(empty)"}
                      </pre>
                    </div>
                  ))
                : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="agent-config-page-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "editor"}
          className={`agent-config-page-tab ${
            activePageTab === "editor" ? "agent-config-page-tab--active" : ""
          }`}
          onClick={() => setActivePageTab("editor")}
        >
          Editor
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "history"}
          className={`agent-config-page-tab ${
            activePageTab === "history" ? "agent-config-page-tab--active" : ""
          }`}
          onClick={handleOpenHistoryTab}
        >
          History{historyVersions.length ? ` (${historyVersions.length})` : ""}
        </button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading draft...</span>
          </div>
        </div>
      ) : activePageTab === "history" ? (
        <div className="agent-config-card agent-config-history-card">
          <div className="agent-config-card-header">
            <label className="agent-config-label">Version history</label>
            {historyLoading ? (
              <span className="agent-config-status agent-config-status--saving">
                Loading...
              </span>
            ) : null}
          </div>

          {historyErrorMessage ? (
            <div className="alert alert-danger py-2 mb-3">{historyErrorMessage}</div>
          ) : null}

          {!historyLoading && historyVersions.length === 0 ? (
            <div className="agent-config-suggestions-empty">
              Nothing has been published yet for this practice. Publish a draft to start the
              history.
            </div>
          ) : (
            <ul className="agent-config-history-list">
              {historyVersions.map((version) => (
                <li key={version.versionId} className="agent-config-history-item">
                  <div className="agent-config-history-item-main">
                    <div className="agent-config-history-item-heading">
                      <span className="agent-config-history-version">
                        Version {version.versionId}
                      </span>
                      {version.versionId === liveVersionId ? (
                        <span className="agent-config-history-live-badge">LIVE</span>
                      ) : null}
                    </div>
                    <div className="agent-config-history-timestamp">
                      Published {formatSavedAt(version.publishedAt) || version.publishedAt}
                    </div>
                    <div className="agent-config-history-preview">
                      {summarizeBundleForHistory(version.bundle)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-light agent-config-history-rollback-btn"
                    onClick={() => handleRequestRollback(version)}
                    disabled={version.versionId === liveVersionId}
                    title={
                      version.versionId === liveVersionId
                        ? "This version is already live"
                        : "Roll back to this version"
                    }
                  >
                    Rollback
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="agent-config-card">
          <div className="agent-config-card-header">
            <label htmlFor="agentConfigPrompt" className="agent-config-label">
              Agent prompt
            </label>
            <span className={`agent-config-status ${statusMeta.className}`}>
              {statusMeta.label}
            </span>
          </div>

          <div className="agent-config-toggle-row">
            <label className="agent-config-toggle-label" htmlFor="agentConfigMultiAgentToggle">
              <input
                id="agentConfigMultiAgentToggle"
                type="checkbox"
                checked={multiAgentEnabled}
                onChange={(event) => handleToggleMultiAgent(event.target.checked)}
              />
              Enable multi-agent mode
            </label>
            <span className="agent-config-toggle-hint">
              Splits this bundle into a router prompt and specialist tabs (by intent),
              all still saved together as one draft.
            </span>
          </div>

          {multiAgentEnabled ? (
            <>
              <div className="agent-config-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTabId === ROUTER_TAB_ID}
                  className={`agent-config-tab ${
                    activeTabId === ROUTER_TAB_ID ? "agent-config-tab--active" : ""
                  }`}
                  onClick={() => setActiveTabId(ROUTER_TAB_ID)}
                >
                  Router
                </button>
                {specialists.map((specialist, index) => (
                  <button
                    key={specialist.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTabId === specialist.id}
                    className={`agent-config-tab ${
                      activeTabId === specialist.id ? "agent-config-tab--active" : ""
                    }`}
                    onClick={() => setActiveTabId(specialist.id)}
                  >
                    {specialist.intent.trim() || `Specialist ${index + 1}`}
                  </button>
                ))}
                <button
                  type="button"
                  className="agent-config-tab-add"
                  onClick={handleAddSpecialist}
                >
                  + Add specialist
                </button>
              </div>

              {activeSpecialist ? (
                <div className="agent-config-specialist-panel">
                  <div className="agent-config-specialist-header">
                    <label
                      htmlFor="agentConfigSpecialistIntent"
                      className="agent-config-label"
                    >
                      Specialist name / intent
                    </label>
                    <button
                      type="button"
                      className="agent-config-remove-specialist"
                      onClick={() => handleRemoveSpecialist(activeSpecialist.id)}
                    >
                      Remove specialist
                    </button>
                  </div>
                  <input
                    id="agentConfigSpecialistIntent"
                    type="text"
                    className="agent-config-intent-input"
                    value={activeSpecialist.intent}
                    onChange={(event) =>
                      handleSpecialistIntentChange(activeSpecialist.id, event.target.value)
                    }
                    placeholder="e.g. billing, scheduling, triage"
                  />
                  <label
                    htmlFor="agentConfigSpecialistPrompt"
                    className="agent-config-label agent-config-label--spaced"
                  >
                    Specialist prompt
                  </label>
                  <textarea
                    id="agentConfigSpecialistPrompt"
                    className="agent-config-textarea"
                    value={activeSpecialist.prompt}
                    onChange={(event) =>
                      handleSpecialistPromptChange(activeSpecialist.id, event.target.value)
                    }
                    placeholder="Write this specialist's instructions..."
                    spellCheck
                  />
                </div>
              ) : (
                routerTextarea
              )}
            </>
          ) : (
            routerTextarea
          )}

          {errorMessage ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">
              {errorMessage}
            </div>
          ) : null}

          {submitErrorMessage ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">
              {submitErrorMessage}
            </div>
          ) : null}

          {suggestionsErrorMessage ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">
              {suggestionsErrorMessage}
            </div>
          ) : null}

          <div className="agent-config-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveNow}
              disabled={saveState === "saving"}
            >
              Save Draft
            </button>
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={handleSubmitDraft}
              disabled={submitState === "submitting"}
            >
              {submitState === "submitting" ? "Submitting..." : "Submit Draft"}
            </button>
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={handleOpenOrGenerateSuggestions}
              disabled={suggestionsButtonDisabled}
              title={suggestionsButtonTitle}
            >
              {suggestionsState === "generating" ? "Generating..." : "AI Suggestions"}
            </button>
            <button
              type="button"
              className={`agent-config-demo-call-btn ${demoCallClassName}`}
              onClick={handleDemoCallClick}
              disabled={demoCallButtonDisabled}
              title={demoCallTitle}
            >
              {demoCallLabel}
            </button>
            <button
              type="button"
              className={`agent-config-publish-btn ${
                publishEnabled
                  ? "agent-config-publish-btn--enabled"
                  : "agent-config-publish-btn--disabled"
              }`}
              onClick={handlePublishClick}
              disabled={!publishEnabled}
              title={publishTitle}
            >
              {publishState === "publishing" ? "Publishing..." : "Publish"}
            </button>
          </div>

          {demoCallError ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">{demoCallError}</div>
          ) : null}

          {demoCallState === DEMO_CALL_STATE.IN_CALL ? (
            <div className="agent-config-demo-call-note">
              Live demo call in progress
              {demoCallVersionId ? ` - testing submitted version ${demoCallVersionId}` : ""}.
              Real calls for this practice keep using the published version until you publish.
            </div>
          ) : null}

          {publishMessage ? (
            <div className="agent-config-demo-call-note">{publishMessage}</div>
          ) : null}

          {/* Issue 9: Demo Call - phone callback mode. Saving the number is a separate,
              explicit action from Call This Number - the button always dials whatever is
              currently saved server-side (testPhoneNumberSavedValue), never the unsaved text
              still sitting in the input. */}
          <div className="agent-config-phone-demo-row">
            <label htmlFor="agentConfigTestPhoneNumber" className="agent-config-label">
              Test phone number
            </label>
            <div className="agent-config-phone-demo-controls">
              <input
                id="agentConfigTestPhoneNumber"
                type="tel"
                className="agent-config-phone-input"
                value={testPhoneNumber}
                onChange={(event) => setTestPhoneNumber(event.target.value)}
                placeholder="+15551234567"
              />
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleSaveTestPhoneNumber}
                disabled={saveTestPhoneNumberDisabled}
                title={testPhoneNumberDirty ? "Save this number" : "Nothing to save"}
              >
                {testPhoneNumberSaveState === "saving" ? "Saving..." : "Save Number"}
              </button>
              <button
                type="button"
                className={`agent-config-call-number-btn ${
                  canCallTestNumber
                    ? "agent-config-call-number-btn--enabled"
                    : "agent-config-call-number-btn--disabled"
                }`}
                onClick={handleCallThisNumberClick}
                disabled={!canCallTestNumber}
                title={callThisNumberTitle}
              >
                {phoneDemoCallState === "calling" ? "Calling..." : "Call This Number"}
              </button>
            </div>
            <span className="agent-config-toggle-hint">
              Saved once, reused on every Call This Number click until you change it. Places a
              real phone call running the current submitted draft.
            </span>

            {testPhoneNumberError ? (
              <div className="alert alert-danger py-2 mt-2 mb-0">{testPhoneNumberError}</div>
            ) : null}

            {phoneDemoCallError ? (
              <div className="alert alert-danger py-2 mt-2 mb-0">{phoneDemoCallError}</div>
            ) : null}

            {phoneDemoCallMessage ? (
              <div className="agent-config-demo-call-note">{phoneDemoCallMessage}</div>
            ) : null}
          </div>
        </div>
      )}

      {!showSuggestionsPanel && suggestions !== null && hasAnySuggestions(suggestions) ? (
        (() => {
          const { totalLocated, totalOther } = countPendingSuggestions(
            suggestions,
            resolvedSuggestionIds,
            prompt,
            specialists,
          );
          const totalPending = totalLocated + totalOther;
          return (
            <button
              type="button"
              className="agent-config-suggestions-tab"
              onClick={() => setShowSuggestionsPanel(true)}
              title="Reopen AI Suggestions - same set, nothing regenerated"
            >
              <span className="agent-config-suggestions-tab-label">AI Suggestions</span>
              {totalPending > 0 ? (
                <span className="agent-config-suggestions-tab-count">{totalPending}</span>
              ) : null}
            </button>
          );
        })()
      ) : null}

      {showSuggestionsPanel ? (
        // No onClick on the backdrop here (unlike the rollback confirm dialog below) - a stray
        // click outside the panel used to both close it AND fire handleDismissSuggestions' mark-
        // as-viewed POST, which felt like the panel "closing itself" while reviewing a long list
        // of suggestions. The panel now only closes via an explicit action: "−" minimizes it to
        // the sidebar tab (suggestions data untouched, reopen any time, same set), and "Continue"
        // additionally marks it viewed to unlock Publish. Neither ever discards `suggestions` -
        // only the "Regenerate" button does that, deliberately, since it calls the AI again.
        <div className="agent-config-suggestions-backdrop">
          <div
            className="agent-config-suggestions-panel"
            role="dialog"
            aria-label="AI Suggestions"
          >
            <div className="agent-config-suggestions-panel-header">
              <h3 className="agent-config-suggestions-title">AI Suggestions</h3>
              <div className="agent-config-suggestions-header-actions">
                <span className="agent-config-suggestions-generated-at">
                  {formatSavedAt(suggestionsGeneratedAt)
                    ? `Generated ${formatSavedAt(suggestionsGeneratedAt)}`
                    : "Advisory - not required to apply"}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary agent-config-suggestions-regenerate-btn"
                  onClick={handleGenerateSuggestions}
                  disabled={suggestionsState === "generating"}
                  title="Run a fresh AI review of the current submitted draft, replacing this set"
                >
                  {suggestionsState === "generating" ? "Regenerating..." : "Regenerate"}
                </button>
                <button
                  type="button"
                  className="agent-config-suggestions-minimize-btn"
                  onClick={handleMinimizeSuggestions}
                  aria-label="Minimize AI Suggestions"
                  title="Tuck this aside and keep editing - reopen it anytime from the tab, same suggestions"
                >
                  −
                </button>
              </div>
            </div>

            {hasAnySuggestions(suggestions) ? (
              <>
                {(() => {
                  const { totalLocated, totalOther } = countPendingSuggestions(
                    suggestions,
                    resolvedSuggestionIds,
                    prompt,
                    specialists,
                  );
                  return (
                    <>
                      <div className="agent-config-suggestions-bulk-row">
                        <span className="agent-config-suggestions-pending-count">
                          {totalLocated} pending suggestion{totalLocated === 1 ? "" : "s"}
                        </span>
                        {totalLocated > 0 ? (
                          <div className="agent-config-suggestions-bulk-actions">
                            <button
                              type="button"
                              className="btn btn-secondary agent-config-suggestions-bulk-btn"
                              onClick={handleRejectAllSuggestions}
                            >
                              Reject all
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary agent-config-suggestions-bulk-btn"
                              onClick={handleAcceptAllEdits}
                            >
                              Accept all
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {/* Only located edits render here - "git diff type suggestions that can
                          be added immediately". Everything else (unlocated edits + notes, e.g.
                          a missing compliance disclosure that isn't a text swap) lives in the
                          collapsed-by-default "Other Findings" section below instead. */}
                      {(() => {
                        const grouped = groupSuggestionsByCategory(suggestions);
                        return SUGGESTION_CATEGORY_LABELS.map(({ key, label }) => {
                          const categoryLocatedEdits = grouped[key].edits.filter(
                            (edit) =>
                              isEditCurrentlyLocated(edit, prompt, specialists) &&
                              !resolvedSuggestionIds.has(edit.id),
                          );
                          if (categoryLocatedEdits.length === 0) {
                            return null;
                          }
                          return (
                            <div key={key} className="agent-config-suggestions-category">
                              <div className="agent-config-suggestions-category-title">
                                {label}
                              </div>
                              {categoryLocatedEdits.map((edit) => (
                                <div key={edit.id} className="agent-config-suggestion-edit-card">
                                  <SuggestionHeader item={edit} />
                                  <div className="agent-config-suggestion-diff">
                                    <div className="agent-config-suggestion-diff-old">
                                      <span className="agent-config-suggestion-diff-marker">
                                        −
                                      </span>
                                      {edit.oldText}
                                    </div>
                                    <div className="agent-config-suggestion-diff-new">
                                      <span className="agent-config-suggestion-diff-marker">
                                        +
                                      </span>
                                      {edit.newText}
                                    </div>
                                  </div>
                                  {edit.rationale ? (
                                    <div className="agent-config-suggestion-rationale">
                                      {edit.rationale}
                                    </div>
                                  ) : null}
                                  <div className="agent-config-suggestion-actions">
                                    <button
                                      type="button"
                                      className="btn btn-secondary agent-config-suggestion-reject-btn"
                                      onClick={() => handleRejectSuggestion(edit)}
                                    >
                                      Reject
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-primary agent-config-suggestion-accept-btn"
                                      onClick={() => handleAcceptEdit(edit)}
                                    >
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        });
                      })()}

                      {totalLocated === 0 && totalOther === 0 ? (
                        <div className="agent-config-suggestions-empty">
                          All suggestions reviewed for this draft.
                        </div>
                      ) : null}

                      {totalOther > 0 ? (
                        <div className="agent-config-other-findings">
                          <div
                            className="agent-config-other-findings-toggle"
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowOtherFindings((current) => !current)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setShowOtherFindings((current) => !current);
                              }
                            }}
                          >
                            <span>
                              {showOtherFindings ? "▾" : "▸"} Other Findings ({totalOther})
                            </span>
                            <button
                              type="button"
                              className="btn btn-secondary agent-config-suggestions-bulk-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDismissAllOtherFindings();
                              }}
                            >
                              Dismiss all
                            </button>
                          </div>

                          {showOtherFindings ? (
                            <div className="agent-config-other-findings-body">
                              {countPendingSuggestions(
                                suggestions,
                                resolvedSuggestionIds,
                                prompt,
                                specialists,
                              ).pendingOtherFindings.map((item) => (
                                <div key={item.id} className="agent-config-suggestion-note-card">
                                  <SuggestionHeader item={item} />
                                  <div className="agent-config-suggestion-note-text">
                                    {item.text || item.rationale || item.newText}
                                    {item.oldText ? (
                                      <span className="agent-config-suggestion-unlocated-tag">
                                        {" "}
                                        (couldn't locate this exact text in the current draft)
                                      </span>
                                    ) : null}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-secondary agent-config-suggestion-reject-btn"
                                    onClick={() => handleRejectSuggestion(item)}
                                  >
                                    Dismiss
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="agent-config-suggestions-empty">
                No suggestions to show for this draft.
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary agent-config-suggestions-dismiss-btn"
              onClick={handleDismissSuggestions}
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {rollbackTargetVersion ? (
        <div className="agent-config-suggestions-backdrop" onClick={handleCancelRollback}>
          <div
            className="agent-config-confirm-dialog"
            role="dialog"
            aria-label="Confirm rollback"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="agent-config-suggestions-title">Roll back to this version?</h3>
            <p className="agent-config-confirm-body">
              This creates a new published version using version{" "}
              {rollbackTargetVersion.versionId}&apos;s content (published{" "}
              {formatSavedAt(rollbackTargetVersion.publishedAt) ||
                rollbackTargetVersion.publishedAt}
              ) and makes it live immediately for real calls. The version you&apos;re rolling
              back from stays in history - nothing is deleted.
            </p>
            <div className="agent-config-confirm-preview">
              {summarizeBundleForHistory(rollbackTargetVersion.bundle)}
            </div>

            {rollbackErrorMessage ? (
              <div className="alert alert-danger py-2 mb-0">{rollbackErrorMessage}</div>
            ) : null}

            <div className="agent-config-confirm-actions">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleCancelRollback}
                disabled={rollbackState === "rolling-back"}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmRollback}
                disabled={rollbackState === "rolling-back"}
              >
                {rollbackState === "rolling-back" ? "Rolling back..." : "Confirm rollback"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
